import { Button } from '@frontdoor/ui/components/button';
import { cn } from '@frontdoor/ui/lib/utils';
import { format } from 'date-fns';
import { Calendar, Truck } from 'lucide-react';

import type { TrackingShipment } from '@/api/generated';

import { TrackingCard } from './TrackingCard.tsx';

const STATUS_PILLS = {
  pending: { label: 'Pending', className: 'bg-sand-100 text-charcoal-500' },
  info_received: { label: 'Label created', className: 'bg-sand-100 text-charcoal-500' },
  in_transit: { label: 'In transit', className: 'bg-teal-50 text-teal-400' },
  out_for_delivery: { label: 'Out for delivery', className: 'bg-teal-50 text-teal-400' },
  available_for_pickup: { label: 'Available for pickup', className: 'bg-teal-50 text-teal-400' },
  delivered: { label: 'Delivered', className: 'bg-hydrangea-200 text-hydrangea-700' },
  failed_attempt: { label: 'Delivery attempted', className: 'bg-marigold-100 text-marigold-900' },
  exception: { label: 'Exception', className: 'bg-rose-200 text-rose-700' },
  expired: { label: 'Expired', className: 'bg-marigold-100 text-marigold-900' },
} as const;

type ShowableStatus = keyof typeof STATUS_PILLS;

// A plain string, not the generated union, so an added status renders no pill rather than
// failing the build until the schema is regenerated.
function isShowableStatus(status: string | null | undefined): status is ShowableStatus {
  return status != null && status in STATUS_PILLS;
}

function formatDeliveryLine(shipment: TrackingShipment): string | null {
  if (shipment.delivered_at) {
    return `Delivered ${format(new Date(shipment.delivered_at), 'MMM d')}`;
  }
  if (shipment.expected_delivery_at) {
    return `Estimated delivery ${format(new Date(shipment.expected_delivery_at), 'MMM d')}`;
  }
  return null;
}

type ShipmentsCardProps = {
  shipments: TrackingShipment[];
};

export function ShipmentsCard({ shipments }: ShipmentsCardProps) {
  if (shipments.length === 0) return null;

  const numbered = shipments.length > 1;

  return (
    <TrackingCard title="Shipments">
      <div className="flex flex-col gap-4">
        {shipments.map((shipment, index) => (
          <PackageRow
            key={shipment.tracking_number ?? index}
            shipment={shipment}
            label={numbered ? `Package ${index + 1}` : 'Package'}
            trackLabel={numbered ? `Track package ${index + 1}` : 'Track package'}
          />
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        This order may arrive in separate packages on different days. We add tracking as each ships.
        Order tracking information may be incomplete.
      </p>
    </TrackingCard>
  );
}

function PackageRow({
  shipment,
  label,
  trackLabel,
}: {
  shipment: TrackingShipment;
  label: string;
  trackLabel: string;
}) {
  const pill = isShowableStatus(shipment.status) ? STATUS_PILLS[shipment.status] : null;
  const showHelper = Boolean(shipment.tracking_url) && !pill;
  const showNumberFallback = Boolean(shipment.tracking_number) && !shipment.tracking_url;
  const deliveryLine = formatDeliveryLine(shipment);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{label}</p>
          {pill && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-[16px] px-2 py-1 text-sm leading-[1.4] whitespace-nowrap',
                pill.className
              )}
            >
              {pill.label}
            </span>
          )}
        </div>
        {shipment.shipping_carrier_name && (
          <div className="flex items-center gap-1 text-sm">
            <Truck className="size-4 shrink-0" aria-hidden />
            <span>{shipment.shipping_carrier_name}</span>
          </div>
        )}
        {deliveryLine && (
          <div className="flex items-center gap-1 text-sm">
            <Calendar className="size-4 shrink-0" aria-hidden />
            <span>{deliveryLine}</span>
          </div>
        )}
        {showNumberFallback && <p className="text-sm">{shipment.tracking_number}</p>}
      </div>
      {shipment.tracking_url && (
        <Button asChild variant="outline" size="sm" className="w-full">
          <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer">
            {trackLabel}
          </a>
        </Button>
      )}
      {showHelper && (
        <p className="text-xs text-muted-foreground">
          Check tracking link for updated delivery status
        </p>
      )}
    </div>
  );
}
